import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { SessionAction } from '@/constants';
import { useCurrentUser } from '@/hooks';
import { Button, Typography } from '@/ui';

import { useGetSessionQuery } from '@/store/api/sessionApi';
import { isCoach } from '@/types/user';

const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/iu;

const statusFromError = (error: unknown): number | undefined => {
  if (!error || typeof error !== 'object' || !('status' in error)) {
    return undefined;
  }
  const { status } = error as { status?: unknown };
  return typeof status === 'number' ? status : undefined;
};

export const canonicalAppointmentActionPath = (
  externalSessionId: string,
  action: string | undefined,
): string | null => {
  if (
    !UUID_PATTERN.test(externalSessionId) ||
    (action !== SessionAction.CANCEL && action !== SessionAction.RESCHEDULE)
  ) {
    return null;
  }

  return `/appointments/external/${externalSessionId}/${action}`;
};

const ExternalSessionRedirect = () => {
  const { externalSessionId = '', action } = useParams();
  const { user: currentUser, isUserLoading } = useCurrentUser();
  const navigate = useNavigate();
  const {
    currentData: session,
    error,
    isFetching,
    isLoading,
    refetch,
  } = useGetSessionQuery(externalSessionId);
  const errorStatus = statusFromError(error);
  const canonicalPath = canonicalAppointmentActionPath(
    externalSessionId,
    action,
  );
  const shouldCanonicalize =
    !session && errorStatus === 404 && canonicalPath !== null;

  useEffect(() => {
    if (!shouldCanonicalize || !canonicalPath) return;
    navigate(canonicalPath, { replace: true });
  }, [canonicalPath, navigate, shouldCanonicalize]);

  useEffect(() => {
    if (
      !session ||
      !session.userId ||
      error ||
      isFetching ||
      isLoading ||
      isUserLoading
    ) {
      return;
    }

    if (isCoach(currentUser) && currentUser.id !== session.userId) {
      const prefix =
        session.coachId === currentUser.id
          ? '/coach/sessions'
          : `/coach/members/${session.userId}/sessions`;
      switch (action) {
        case SessionAction.GROWTH:
          navigate(`/coach/members/${session.userId}/growth`, {
            replace: true,
          });
          break;
        case SessionAction.RESCHEDULE:
          navigate(
            `${prefix}/${session.id}?action=${SessionAction.RESCHEDULE}`,
            { replace: true },
          );
          break;
        case SessionAction.CANCEL:
          navigate(`${prefix}/${session.id}?action=${SessionAction.CANCEL}`, {
            replace: true,
          });
          break;
        case SessionAction.VIEW:
        default:
          navigate(`${prefix}/${session.id}`, { replace: true });
          break;
      }
      return;
    }

    switch (action) {
      case SessionAction.GROWTH:
        navigate('/m/growth', { replace: true });
        break;
      case SessionAction.RESCHEDULE:
        navigate(
          `/m/sessions/${session.id}?action=${SessionAction.RESCHEDULE}`,
          { replace: true },
        );
        break;
      case SessionAction.CANCEL:
        navigate(`/m/sessions/${session.id}?action=${SessionAction.CANCEL}`, {
          replace: true,
        });
        break;
      case SessionAction.VIEW:
      default:
        navigate(`/m/sessions/${session.id}`, { replace: true });
        break;
    }
  }, [
    action,
    currentUser,
    error,
    isFetching,
    isLoading,
    isUserLoading,
    navigate,
    session,
  ]);

  if (isFetching || isLoading || isUserLoading || shouldCanonicalize) {
    return <Typography>Loading session...</Typography>;
  }

  if (errorStatus === 403) {
    return <Typography>You do not have access to this session</Typography>;
  }

  if (errorStatus === 404) {
    return <Typography>Session not found</Typography>;
  }

  if (error) {
    return (
      <div>
        <Typography>Unable to load session</Typography>
        <Button variant="outlined" onClick={refetch}>
          Retry
        </Button>
      </div>
    );
  }

  return <Typography>Redirecting to session...</Typography>;
};

export default ExternalSessionRedirect;
